import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatCurrency } from '@/lib/formatters'
import { type PipelineDeal, type PipelineStats } from '@/models/myPipeline'
import { CompactDealCard, DealRow } from './DealRow'

const COLUMNS = ['Company', 'Contact', 'Deal value', 'Stage', 'Next action', 'Due', '']

const WHOLE_DOLLARS = { minimumFractionDigits: 0, maximumFractionDigits: 0 }

interface DealsTableProps {
  deals: PipelineDeal[]
  previousMonthStats: PipelineStats | undefined
  previousMonthLabel: string
  canEditDueDates: boolean
}

function PreviousMonthSummary({ stats, label }: { stats: PipelineStats; label: string }) {
  return (
    <span>
      {label}: {stats.won} won · {stats.lost} lost ·{' '}
      {formatCurrency(Number(stats.totalPipeline), WHOLE_DOLLARS)} in pipeline
    </span>
  )
}

export function DealsTable({
  deals,
  previousMonthStats,
  previousMonthLabel,
  canEditDueDates,
}: DealsTableProps) {
  if (deals.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-muted-foreground text-sm">No deals in this month yet.</p>
      </div>
    )
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableHead
                    key={column}
                    className={column === 'Deal value' ? 'text-right font-semibold' : 'font-semibold'}
                  >
                    {column}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {deals.map((deal) => (
                <DealRow key={deal.id} deal={deal} canEditDueDates={canEditDueDates} />
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col gap-2 lg:hidden">
          {deals.map((deal) => (
            <CompactDealCard key={deal.id} deal={deal} canEditDueDates={canEditDueDates} />
          ))}
        </div>

        <div className="text-muted-foreground flex flex-wrap justify-between gap-2 text-xs">
          <span>
            Deal value auto-fills from the bound policy or the latest quote. Click a value to override it;
            clear it to go back to auto.
          </span>
          {previousMonthStats && (
            <PreviousMonthSummary stats={previousMonthStats} label={previousMonthLabel} />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
